import { makeAutoObservable } from "mobx"

import type { BlogPostMetadata } from "../types"

interface MDXModule {
  default: React.ComponentType
  frontmatter?: BlogPostMetadata
}

// Vite glob import - lazy loads all MDX files for components
// Each file becomes a separate chunk automatically
const blogModules = import.meta.glob<MDXModule>(
  "@/content/blog/**/*.mdx",
  { eager: false }, // Lazy load - code splitting!
)

// Eager import to get frontmatter for all posts immediately
// Note: This will load all modules eagerly, but we only access frontmatter
// The components remain code-split and are only used when loadPost is called
const blogModulesEager = import.meta.glob<MDXModule>(
  "@/content/blog/**/*.mdx",
  { eager: true }, // Eager load to get frontmatter (components loaded but not used)
)

export class BlogStore {
  private loadedPosts = new Map<string, MDXModule["default"]>()
  private metadataCache: BlogPostMetadata[] | null = null

  constructor() {
    makeAutoObservable(this)
  }

  get allPosts(): BlogPostMetadata[] {
    if (!this.metadataCache) {
      // Extract metadata from module paths and eagerly loaded frontmatter
      this.metadataCache = Object.keys(blogModules)
        .map((path) => {
          const slug = path.replace(/^.*\/blog\//, "").replace(/\.mdx$/, "")

          // Get frontmatter from eager import
          const eagerModule = blogModulesEager[path] as MDXModule | undefined
          const frontmatter = eagerModule?.frontmatter

          return {
            slug,
            title: frontmatter?.title ?? slug,
            date: frontmatter?.date,
            description: frontmatter?.description,
            category: frontmatter?.category,
            order: frontmatter?.order,
            authorName: frontmatter?.authorName,
            authorInitials: frontmatter?.authorInitials,
            authorRole: frontmatter?.authorRole,
            readTimeMinutes: frontmatter?.readTimeMinutes,
            path,
          }
        })
        .sort((a, b) => {
          // Sort by date if available, otherwise by slug
          if (a.date && b.date) {
            return new Date(b.date).getTime() - new Date(a.date).getTime()
          }
          return b.slug.localeCompare(a.slug)
        })
    }
    return this.metadataCache
  }

  getPostBySlug(slug: string): BlogPostMetadata | undefined {
    return this.allPosts.find((p) => p.slug === slug)
  }

  async loadPost(slug: string): Promise<{
    Component: React.ComponentType
    frontmatter: BlogPostMetadata
  }> {
    // Check cache
    if (this.loadedPosts.has(slug)) {
      const Component = this.loadedPosts.get(slug)!
      const metadata = this.getPostBySlug(slug)!
      return { Component, frontmatter: metadata }
    }

    // Find matching module path
    const modulePath = Object.keys(blogModules).find((path) => {
      const pathSlug = path.replace(/^.*\/blog\//, "").replace(/\.mdx$/, "")
      return pathSlug === slug
    })

    if (!modulePath) {
      throw new Error(`Blog post not found: ${slug}`)
    }

    // Vite handles dynamic import and code splitting!
    const module = await blogModules[modulePath]()

    if (!module.default) {
      throw new Error(`Invalid MDX module: ${slug}`)
    }

    // Cache component
    this.loadedPosts.set(slug, module.default)

    // Metadata already has frontmatter from eager import, but update if module has newer data
    const metadata = this.getPostBySlug(slug)!
    if (module.frontmatter) {
      Object.assign(metadata, module.frontmatter)
    }

    return {
      Component: module.default,
      frontmatter: metadata,
    }
  }

  isPostLoaded(slug: string): boolean {
    return this.loadedPosts.has(slug)
  }
}
